<kanzi-ui-main-card
  title="{{ 'locations.ubicaciones.title' | translate }}"
  description="{{ 'locations.ubicaciones.description' | translate }}"
>
  <ng-template kTemplate="rightTopContent">
    <div class="flex gap-2">
      <p-button
        icon="pi pi-refresh"
        [outlined]="true"
        severity="secondary"
        (onClick)="onRefresh()"
        [pTooltip]="'locations.ubicaciones.actions.refresh' | translate"
        tooltipPosition="bottom"
      ></p-button>
      <p-button
        [label]="'locations.ubicaciones.actions.create' | translate"
        icon="pi pi-plus"
        (onClick)="onCreate()"
      ></p-button>
    </div>
  </ng-template>

  <!-- Breadcrumb: real navigation path from customer root to current level -->
  <div class="mb-3">
    <p-breadcrumb
      [model]="breadcrumbItems()"
      [home]="breadcrumbHome()"
      (onItemClick)="onBreadcrumbItemClick($event)"
    ></p-breadcrumb>
  </div>

  <!-- Filters Section (apply within the current level) -->
  <div class="grid mb-4">
    <div class="col-12 md:col-6 lg:col-4">
      <span class="p-input-icon-left w-full">
        <i class="pi pi-search"></i>
        <input
          pInputText
          type="text"
          [placeholder]="'locations.ubicaciones.search.placeholder' | translate"
          class="w-full"
          [ngModel]="searchText()"
          (ngModelChange)="searchText.set($event)"
        />
      </span>
    </div>
    <div class="col-12 md:col-6 lg:col-4">
      <p-dropdown
        [options]="locationTypes()"
        [placeholder]="'locations.ubicaciones.search.filterByType' | translate"
        optionLabel="label"
        optionValue="value"
        [showClear]="true"
        class="w-full"
        [ngModel]="selectedType()"
        (ngModelChange)="selectedType.set($event)"
        [loading]="isTypesLoading()"
      ></p-dropdown>
    </div>
    <div class="col-12 md:col-12 lg:col-4">
      <p-button
        [label]="'locations.ubicaciones.search.clearFilters' | translate"
        icon="pi pi-filter-slash"
        [outlined]="true"
        severity="secondary"
        (onClick)="onClearFilters()"
        class="w-full"
      ></p-button>
    </div>
  </div>

  <!-- Loading Skeleton -->
  @if (isLoading()) {
    <div class="border-round surface-border surface-card p-4">
      <p-skeleton height="3rem" styleClass="mb-3"></p-skeleton>
      <p-skeleton height="3rem" styleClass="mb-3"></p-skeleton>
      <p-skeleton height="3rem" styleClass="mb-3"></p-skeleton>
      <p-skeleton height="3rem"></p-skeleton>
    </div>
  }

  <!-- Table with Pagination (always shows the current level) -->
  @if (!isLoading()) {
    <p-table
      [value]="tableData()"
      [columns]="cols"
      [paginator]="true"
      [rows]="rows()"
      [totalRecords]="totalRecords()"
      [lazy]="true"
      (onLazyLoad)="onPageChange($event)"
      [first]="first()"
      [rowsPerPageOptions]="[10, 25, 50, 100]"
      [showCurrentPageReport]="true"
      currentPageReportTemplate="{{ 'locations.ubicaciones.pagination.showing' | translate }}"
      styleClass="p-datatable-sm"
      [tableStyle]="{ 'min-width': '50rem' }"
    >
      <!-- Header -->
      <ng-template pTemplate="header" let-columns>
        <tr>
          @for (col of columns; track col.field) {
            <th [style.width]="col.width">
              {{ col.header | translate }}
            </th>
          }
        </tr>
      </ng-template>

      <!-- Body -->
      <ng-template pTemplate="body" let-location let-columns="columns">
        <tr
          (mouseenter)="onRowHover(location)"
          (click)="onDrillDown(location)"
          class="hover:surface-hover cursor-pointer"
        >
          <!-- Name -->
          <td>
            <div class="flex align-items-center gap-2">
              <i class="pi pi-angle-right text-500"></i>
              <span class="font-medium">{{ location.name }}</span>
            </div>
          </td>

          <!-- Display Name -->
          <td>
            <span>{{ location.display_name || location.name }}</span>
          </td>

          <!-- Type -->
          <td>
            <span class="font-semibold text-primary">
              {{ location.type?.display_name || location.type?.name || '-' }}
            </span>
          </td>

          <!-- External ID -->
          <td>
            <span class="text-500">{{ location.external_id || '-' }}</span>
          </td>

          <!-- Parent -->
          <td>
            <span class="text-600">{{ location.parent?.name || '-' }}</span>
          </td>

          <!-- Actions -->
          <td>
            <div class="flex gap-2">
              <p-button
                icon="pi pi-eye"
                [rounded]="true"
                [text]="true"
                severity="info"
                size="small"
                (onClick)="$event.stopPropagation(); onDetail(location)"
                [pTooltip]="'locations.ubicaciones.actions.view' | translate"
                tooltipPosition="top"
              ></p-button>
              <p-button
                icon="pi pi-pencil"
                [rounded]="true"
                [text]="true"
                severity="success"
                size="small"
                (onClick)="$event.stopPropagation(); onEdit(location)"
                [pTooltip]="'locations.ubicaciones.actions.edit' | translate"
                tooltipPosition="top"
              ></p-button>
              <p-button
                icon="pi pi-trash"
                [rounded]="true"
                [text]="true"
                severity="danger"
                size="small"
                (onClick)="$event.stopPropagation(); onDelete(location)"
                [pTooltip]="'locations.ubicaciones.actions.delete' | translate"
                tooltipPosition="top"
              ></p-button>
            </div>
          </td>
        </tr>
      </ng-template>

      <!-- Empty State: current level has no children -->
      <ng-template pTemplate="emptymessage">
        <tr>
          <td [attr.colspan]="cols.length" class="text-center p-5">
            <div class="flex flex-column align-items-center gap-3">
              <i class="pi pi-map-marker text-5xl text-400"></i>
              <p class="text-600 font-medium">
                {{ 'locations.ubicaciones.emptyLevel.title' | translate }}
              </p>
              <p class="text-500">
                {{ 'locations.ubicaciones.emptyLevel.message' | translate }}
              </p>
              <p-button
                [label]="'locations.ubicaciones.emptyTable.createButton' | translate"
                icon="pi pi-plus"
                (onClick)="onCreate()"
              ></p-button>
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>
  }

  <!-- Error State -->
  @if (listQuery.isError()) {
    <p-message
      severity="error"
      [text]="'locations.ubicaciones.errors.loadError' | translate"
    ></p-message>
  }
</kanzi-ui-main-card>

<p-toast></p-toast>
<p-confirmDialog></p-confirmDialog>
